import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Trophy,
  TrendingUp,
  Target,
  ChevronRight,
  BarChart,
  Database,
  Code,
  Award,
  History,
  Crown,
  Briefcase,
  Lock,
  GraduationCap,
  Code2,
  FileSearch,
  PlayCircle,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { useQuizStats } from "@/hooks/useQuizStats";
import { useAcademyProgress } from "@/hooks/useAcademyProgress";

type Category = "power_bi" | "sql" | "python";

type AcademyView = "home" | "trail" | "certificates" | "history" | "ranking" | "simulations" | "challenges" | "resume-analyzer";

const trailConfig: Record<Category, {
  title: string;
  description: string;
  icon: typeof Database;
  iconBg: string;
}> = {
  power_bi: {
    title: "Power BI",
    description: "DAX, modelagem, visuais e Power Query",
    icon: BarChart,
    iconBg: "bg-amber-500/15 text-amber-600",
  },
  sql: {
    title: "SQL",
    description: "Consultas, joins, agregações e otimização",
    icon: Database,
    iconBg: "bg-blue-500/15 text-blue-600",
  },
  python: {
    title: "Python",
    description: "Pandas, NumPy e análise de dados",
    icon: Code,
    iconBg: "bg-yellow-500/15 text-yellow-600",
  },
};

const categories: Category[] = ["power_bi", "sql", "python"];

interface AcademyHomeProps {
  onNavigate: (view: AcademyView, category?: Category) => void;
  onSelectTrail?: (category: Category) => void;
  hasPremiumAccess: boolean;
  hasBasicAccess: boolean;
  planType?: "free" | "trial" | "premium";
  onUpgrade: () => void;
  onStartTrial?: () => void;
}

export function AcademyHome({
  onNavigate,
  onSelectTrail,
  hasPremiumAccess,
  hasBasicAccess,
  planType,
  onUpgrade,
  onStartTrial,
}: AcademyHomeProps) {
  const { userMetrics, categoryStats, loading } = useQuizStats();
  const { data: nextStep, isLoading: progressLoading } = useAcademyProgress();

  const hasUnlockedApply = (nextStep?.completedCount ?? 0) >= 1;

  const goTrail = (cat: Category) => {
    if (!hasBasicAccess) {
      onUpgrade();
      return;
    }
    if (onSelectTrail) onSelectTrail(cat);
    else onNavigate("trail", cat);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
          <GraduationCap className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Academy</h1>
          <p className="text-sm text-muted-foreground">
            Sua jornada em dados, passo a passo.
          </p>
        </div>
      </div>

      {/* Banner de iniciar trial — só para usuários free */}
      {planType === "free" && onStartTrial && (
        <Card className="border-primary/30 bg-gradient-to-r from-primary/10 to-primary/5">
          <CardContent className="p-4 md:p-5 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
                <Sparkles className="h-5 w-5" />
              </div>
              <div>
                <p className="font-semibold text-foreground">Ative seu trial gratuito de 7 dias</p>
                <p className="text-sm text-muted-foreground">
                  Acesse o 1º módulo de cada trilha. Sem cartão, sem cobrança.
                </p>
              </div>
            </div>
            <Button onClick={onStartTrial} className="gap-1.5">
              <Sparkles className="h-4 w-4" />
              Iniciar meu trial
            </Button>
          </CardContent>
        </Card>
      )}

      {/* HERO — Continue de onde parou */}
      {progressLoading ? (
        <Skeleton className="h-40 w-full rounded-xl" />
      ) : (
        <Card className="border-primary/20 bg-gradient-to-br from-primary/5 via-primary/[0.02] to-background overflow-hidden">
          <CardContent className="p-6 md:p-8">
            {nextStep?.status === "all_completed" ? (
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-emerald-500/15 text-emerald-600 flex items-center justify-center shrink-0">
                  <Trophy className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <Badge className="mb-2 bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15">
                    Trilhas concluídas
                  </Badge>
                  <h2 className="text-xl font-semibold text-foreground">
                    Você terminou todas as trilhas! 🎉
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1 mb-4">
                    Hora de aplicar: emita seu certificado, resolva desafios e teste-se numa simulação.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => onNavigate("certificates")}>
                      <Award className="h-4 w-4 mr-1.5" /> Meus certificados
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onNavigate("challenges")}>
                      Ir para desafios
                    </Button>
                  </div>
                </div>
              </div>
            ) : nextStep?.hasAnyProgress && nextStep.category ? (
              <div className="flex items-start gap-4">
                <div
                  className={`h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ${
                    trailConfig[nextStep.category].iconBg
                  }`}
                >
                  <PlayCircle className="h-6 w-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <Badge variant="secondary" className="mb-2 text-xs">
                    Continue de onde parou
                  </Badge>
                  <h2 className="text-xl font-semibold text-foreground truncate">
                    {nextStep.moduleTitle}
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Trilha de {trailConfig[nextStep.category].title} · {nextStep.completedCount} de{" "}
                    {nextStep.totalCount} módulos concluídos
                  </p>
                  <div className="mt-3 mb-4">
                    <Progress
                      value={
                        nextStep.totalCount > 0
                          ? Math.round((nextStep.completedCount / nextStep.totalCount) * 100)
                          : 0
                      }
                      className="h-1.5"
                    />
                  </div>
                  <Button onClick={() => goTrail(nextStep.category!)} className="gap-1.5">
                    Continuar módulo
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                <div className="h-12 w-12 rounded-xl bg-primary/15 text-primary flex items-center justify-center shrink-0">
                  <Sparkles className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <Badge variant="secondary" className="mb-2 text-xs">
                    Comece aqui
                  </Badge>
                  <h2 className="text-xl font-semibold text-foreground">
                    Escolha sua primeira trilha
                  </h2>
                  <p className="text-sm text-muted-foreground mt-1 mb-4">
                    Vamos fazer um diagnóstico rápido de 3 perguntas para sugerir o melhor ponto de
                    partida. A jornada é sequencial: aula → exercícios → prova → próximo módulo.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {categories.map((cat) => (
                      <Button
                        key={cat}
                        size="sm"
                        variant="outline"
                        onClick={() => goTrail(cat)}
                        className="gap-1.5"
                      >
                        {trailConfig[cat].title}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {loading ? (
          [...Array(3)].map((_, i) => (
            <Card key={i} className="border-border/50">
              <CardContent className="p-4">
                <Skeleton className="h-4 w-12 mb-1" />
                <Skeleton className="h-3 w-20" />
              </CardContent>
            </Card>
          ))
        ) : (
          <>
            <Card className="border-border/50">
              <CardContent className="p-4 flex items-center gap-3">
                <Trophy className="h-5 w-5 text-amber-500 shrink-0" />
                <div className="min-w-0">
                  <div className="text-lg font-bold leading-tight">{userMetrics.totalAttempts}</div>
                  <div className="text-xs text-muted-foreground">Questões respondidas</div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="p-4 flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-emerald-500 shrink-0" />
                <div className="min-w-0">
                  <div className="text-lg font-bold leading-tight">{userMetrics.accuracy}%</div>
                  <div className="text-xs text-muted-foreground">Taxa de acerto</div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-border/50">
              <CardContent className="p-4 flex items-center gap-3">
                <Target className="h-5 w-5 text-primary shrink-0" />
                <div className="min-w-0">
                  <div className="text-lg font-bold leading-tight">{userMetrics.totalPoints}</div>
                  <div className="text-xs text-muted-foreground">Pontos acumulados</div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Trilhas — escolher outra */}
      <section>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Minhas trilhas</h2>
          <span className="text-xs text-muted-foreground">Aprendizado sequencial</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {categories.map((cat) => {
            const config = trailConfig[cat];
            const stat = categoryStats.find((s) => s.category === cat);
            const attempts = stat?.userAttempts || 0;
            const total = stat?.totalQuestions || 0;
            const progress = total > 0 ? Math.min(Math.round((attempts / total) * 100), 100) : 0;
            const hasStarted = attempts > 0;

            return (
              <Card
                key={cat}
                className="group cursor-pointer border-border/50 hover:border-primary/30 hover:shadow-md transition-all"
                onClick={() => goTrail(cat)}
              >
                <CardContent className="p-5 space-y-3">
                  <div className="flex items-start justify-between">
                    <div
                      className={`h-11 w-11 rounded-xl ${config.iconBg} flex items-center justify-center`}
                    >
                      <config.icon className="h-5 w-5" />
                    </div>
                    {hasStarted && (
                      <Badge variant="outline" className="text-[10px]">
                        {progress}%
                      </Badge>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors">
                      {config.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">{config.description}</p>
                  </div>
                  {hasStarted && <Progress value={progress} className="h-1.5" />}
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-1 group-hover:bg-primary group-hover:text-primary-foreground transition-colors"
                  >
                    {hasStarted ? "Continuar" : "Começar trilha"}
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Aplicar — desbloqueia após 1º módulo */}
      <section>
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">Aplicar o que aprendeu</h2>
          {!hasUnlockedApply && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Lock className="h-3 w-3" /> Conclua 1 módulo para liberar
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <ApplyCard
            icon={Code2}
            title="Desafios de código"
            description="Resolva problemas reais de SQL, Python e Power BI"
            locked={!hasUnlockedApply}
            onClick={() => onNavigate("challenges")}
          />
          <ApplyCard
            icon={Briefcase}
            title="Simulador de entrevista"
            description="Pratique com IA como se fosse recrutador real"
            locked={!hasUnlockedApply || !hasPremiumAccess}
            isPro={!hasPremiumAccess}
            onClick={() => (hasPremiumAccess ? onNavigate("simulations") : onUpgrade())}
          />
          <ApplyCard
            icon={FileSearch}
            title="Analisador de currículo"
            description="Receba feedback com IA sobre seu CV"
            locked={!hasUnlockedApply || !hasPremiumAccess}
            isPro={!hasPremiumAccess}
            onClick={() => (hasPremiumAccess ? onNavigate("resume-analyzer") : onUpgrade())}
          />
        </div>
      </section>

      {/* Progresso e reconhecimento */}
      <section>
        <h2 className="text-lg font-semibold text-foreground mb-4">Progresso e reconhecimento</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <QuickCard icon={Award} title="Certificados" onClick={() => onNavigate("certificates")} />
          <QuickCard icon={History} title="Histórico" onClick={() => onNavigate("history")} />
          <QuickCard icon={Crown} title="Ranking" onClick={() => onNavigate("ranking")} />
        </div>
      </section>
    </div>
  );
}

interface ApplyCardProps {
  icon: typeof Code2;
  title: string;
  description: string;
  locked: boolean;
  isPro?: boolean;
  onClick: () => void;
}

function ApplyCard({ icon: Icon, title, description, locked, isPro, onClick }: ApplyCardProps) {
  return (
    <Card
      className={`cursor-pointer border-border/50 transition-all ${
        locked
          ? "opacity-70 hover:opacity-100"
          : "hover:border-primary/30 hover:shadow-md"
      }`}
      onClick={onClick}
    >
      <CardContent className="p-4 flex items-start gap-3">
        <div className="relative shrink-0">
          <Icon className="h-6 w-6 text-muted-foreground" />
          {locked && (
            <Lock className="h-3 w-3 text-muted-foreground absolute -top-1 -right-1" />
          )}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-medium text-foreground">{title}</span>
            {isPro && (
              <Badge variant="secondary" className="text-[9px] px-1 py-0 bg-amber-500/20 text-amber-700">
                PRO
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function QuickCard({ icon: Icon, title, onClick }: { icon: typeof Award; title: string; onClick: () => void }) {
  return (
    <Card
      className="cursor-pointer border-border/50 hover:border-primary/30 hover:shadow-md transition-all"
      onClick={onClick}
    >
      <CardContent className="p-4 flex items-center gap-3">
        <Icon className="h-5 w-5 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">{title}</span>
      </CardContent>
    </Card>
  );
}
